import { Button, Radio, RadioChangeEvent, Table } from 'antd';
import { useAppSelector } from 'app/store';
import { uiModel } from 'entities/ui';
import { ImportPreviewTableRow } from 'entities/ui/model/absence';
import { useAbsenceFileUploadMutation, useAbsenceImportMutation } from 'app/services/edpApiV1/absence';
import { memo, useState } from 'react';
import { SorterResult } from 'antd/lib/table/interface';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import dayjs from 'dayjs';
import { PICKER_DATE_MASK, SERVER_DATE_MASK } from 'shared/constants';
import { importPreviewColumns } from '../../config';
import { EditableCell } from './editable-cell';
import style from './index.module.scss';
import { AlertMessage } from './alert-message';
type EditableTableProps = Parameters<typeof Table>[0];
export type ColumnTypes = Exclude<EditableTableProps['columns'], undefined | ImportPreviewTableRow>;

const sortOptions = [
  { label: 'Cортировать по номеру строки', value: '' },
  { label: 'Сортировать по табельному номеру', value: 'personelNumber' },
  { label: 'Поднять невалидные строки', value: 'id' },
];

export const Preview = memo(() => {
  const [sortedInfo, setSortedInfo] = useState<SorterResult<ColumnTypes[number]>>({ columnKey: '' });

  const [trigger, responseUploadFile] = useAbsenceFileUploadMutation({
    fixedCacheKey: 'uploadFile',
  });
  const [triggerImport] = useAbsenceImportMutation({ fixedCacheKey: 'absenceImport' });

  const dataTable = useAppSelector(uiModel.Absence.selectors.selectAllRows) || [];
  const dateErrors = useAppSelector(uiModel.Absence.selectors.getDateErrors);
  const duplicateErrors = useAppSelector(uiModel.Absence.selectors.getDuplicateErrors);
  const abscenseData = useAppSelector(uiModel.Absence.selectors.getAbsenceData);
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);
  const absenceAmount = dataTable?.filter(item => !item.errors?.employee).length;
  const isDisabled = dateErrors.length !== 0 || duplicateErrors.length !== 0 || !absenceAmount;

  const columns = importPreviewColumns(sortedInfo).map(col => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record: ImportPreviewTableRow) => {
        return {
          record,
          rowId: record.tableId,
          editable: col.editable ? 1 : 0,
          dataIndex: col.dataIndex,
          title: col.title,
          editing: !record.errors?.employee,
          startedAt: record.startedAt,
          endedAt: record.endedAt,
        };
      },
    };
  });

  const components = {
    body: {
      cell: EditableCell,
    },
  };

  const getRowClassName = (record: ImportPreviewTableRow): string => {
    if (
      record.errors?.employee ||
      record.errors?.duplicate ||
      record.errors?.interval ||
      record.errors?.date ||
      !record.startedAt ||
      !record.endedAt
    ) {
      return style.error;
    }
    return '';
  };

  const onClick = () => {
    if (!abscenseData) {
      return;
    }
    const mappedData = abscenseData.map(elem => ({
      ...elem,
      startedAt: dayjs(elem.startedAt, PICKER_DATE_MASK).format(SERVER_DATE_MASK),
      endedAt: dayjs(elem.endedAt, PICKER_DATE_MASK).format(SERVER_DATE_MASK),
    }));
    triggerImport(mappedData);
  };

  const onChange = ({ target: { value } }: RadioChangeEvent) => {
    setSortedInfo({ order: 'descend', columnKey: value });
  };

  return (
    <div className={style.root}>
      <div className={style.import}>
        <div className={style.absenceAmount}>
          <b>Количество неявок: {absenceAmount}</b>
          {responseUploadFile.data?.errors?.length && <span>{responseUploadFile.data?.errors.length}</span>}
        </div>
        {!allAvailableOrganizations && (
          <Button type="primary" onClick={onClick} disabled={isDisabled}>
            Отправить
          </Button>
        )}
      </div>
      <AlertMessage />

      <Table
        rowKey={'tableId'}
        showSorterTooltip={false}
        title={() => (
          <Radio.Group options={sortOptions} value={sortedInfo.columnKey} onChange={onChange} optionType="button" />
        )}
        dataSource={dataTable}
        columns={columns as ColumnTypes}
        components={components}
        bordered
        pagination={false}
        rowClassName={record => getRowClassName(record as ImportPreviewTableRow)}
        scroll={{ x: true, ...(dataTable.length >= 10 ? { y: 600 } : {}) }}
      />
    </div>
  );
});
